import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { useNavigate } from "react-router-dom";
import {
  getDoc,
  getDocs,
  doc,
  updateDoc,
  arrayUnion,
  collection,
  query,
  where,
  addDoc,
  deleteDoc,
} from "firebase/firestore";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Container,
  Navbar,
  Form,
  FormControl,
  Button,
  ListGroup,
  Image,
  Modal,
  Badge,
} from "react-bootstrap";
import {
  FaUserPlus,
  FaSignOutAlt,
  FaSearch,
  FaEye,
  FaUsers,
  FaPlus,
  FaTrash,
} from "react-icons/fa";

function Profile() {
  const [user, setUser] = useState(null);
  const [friends, setFriends] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [searchUserId, setSearchUserId] = useState("");
  const [showUid, setShowUid] = useState(false);

  // stateの追加（既存のuseStateと合わせて）
  const [groupName, setGroupName] = useState("");
  const [selectedFriends, setSelectedFriends] = useState([]); // 追加した友達のリスト
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false); // グループ作成モーダルの状態
  const [groups, setGroups] = useState([]); // ユーザーが所属しているグループ一覧
  const [unreadGroupCounts, setUnreadGroupCounts] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await fetchFriends(currentUser.uid);
      } else {
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const fetchFriends = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        const friendIds = userDoc.data().friends || [];
        const friendData = [];
        const unreadData = {};

        for (const friendId of friendIds) {
          const friendDoc = await getDoc(doc(db, "users", friendId));
          if (friendDoc.exists()) {
            friendData.push({
              id: friendId,
              name: friendDoc.data().name,
              img: friendDoc.data().profileImage,
            });
            unreadData[friendId] = await fetchUnreadMessages(uid, friendId);
          }
        }

        setFriends(friendData);
        setUnreadCounts(unreadData);
      }
    } catch (error) {
      console.error("フレンドデータ取得エラー:", error);
    }
  };

  const fetchUnreadMessages = async (uid, friendId) => {
    const chatId = uid < friendId ? `${uid}_${friendId}` : `${friendId}_${uid}`;
    const messagesRef = collection(db, "chats", chatId, "messages");
    const q = query(
      messagesRef,
      where("isRead", "==", false),
      where("sender", "==", friendId)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  };

  const handleFriendSearch = async () => {
    try {
      const searchedUserDoc = await getDoc(doc(db, "users", searchUserId));
      if (!searchedUserDoc.exists()) {
        alert("ユーザーが見つかりません");
        return;
      }

      const currentUserRef = doc(db, "users", user.uid);
      await updateDoc(currentUserRef, {
        friends: arrayUnion(searchUserId),
      });

      const searchedUserRef = doc(db, "users", searchUserId);
      await updateDoc(searchedUserRef, {
        friends: arrayUnion(user.uid),
      });

      alert("友達を追加しました！");
      await fetchFriends(user.uid);
      setShowModal(false);
      setSearchUserId("");
    } catch (error) {
      console.error("友達追加エラー:", error);
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate("/");
  };

  // グループ作成
  const handleCreateGroup = async () => {
    if (selectedFriends.length === 0) {
      alert("グループに追加する友達を選んでください！");
      return;
    }
    try {
      const groupRef = await addDoc(collection(db, "groups"), {
        name: groupName,
        createdBy: user.uid,
        members: [...selectedFriends, user.uid],
      });
      console.log("グループ作成成功:", groupRef.id);
      setGroupName("");
      setSelectedFriends([]);
      setIsGroupModalOpen(false);
      fetchGroups(); // 作成後に再取得
    } catch (error) {
      console.error("グループ作成エラー:", error);
    }
  };

  // 自分が所属しているグループ一覧取得
  const fetchGroups = async () => {
    const q = query(
      collection(db, "groups"),
      where("members", "array-contains", user.uid)
    );
    const snapshot = await getDocs(q);
    const groupList = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setGroups(groupList);

    const counts = {};
    for (const group of groupList) {
      const messagesRef = collection(db, "groups", group.id, "messages");
      const messagesSnap = await getDocs(messagesRef);

      let unreadCount = 0;
      messagesSnap.forEach((doc) => {
        const data = doc.data();

        // 自分が送信したメッセージは無視、かつ未読フラグがfalseならカウント
        if (data.sender !== user.uid && data.isRead?.[user.uid] === false) {
          unreadCount++;
        }
      });

      counts[group.id] = unreadCount;
    }

    setUnreadGroupCounts(counts);
  };

  // グループの未読メッセージ数取得
  const fetchUnreadGroupMessages = async (groupId) => {
    const groupRef = doc(db, "groups", groupId);
    const groupDoc = await getDoc(groupRef);
    if (groupDoc.exists()) {
      const groupMembers = groupDoc.data().members;
      const unreadCountsForGroup = {};

      for (const memberId of groupMembers) {
        const messagesRef = collection(db, "groups", groupId, "messages");
        const q = query(
          messagesRef,
          where("isRead", "==", false),
          where("sender", "==", memberId)
        );
        const snapshot = await getDocs(q);
        unreadCountsForGroup[memberId] = snapshot.size;
      }

      return unreadCountsForGroup;
    }
    return {};
  };

  const handleDeleteFriend = async (friendId) => {
    if (!window.confirm("本当にこの友達を削除しますか？")) return;

    try {
      const userRef = doc(db, "users", user.uid);
      const friendRef = doc(db, "users", friendId);

      // 自分のfriendsから削除
      await updateDoc(userRef, {
        friends: (await getDoc(userRef))
          .data()
          .friends.filter((id) => id !== friendId),
      });

      // 相手のfriendsから自分を削除
      await updateDoc(friendRef, {
        friends: (await getDoc(friendRef))
          .data()
          .friends.filter((id) => id !== user.uid),
      });

      await fetchFriends(user.uid);
      alert("友達を削除しました。");
    } catch (error) {
      console.error("友達削除エラー:", error);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm("本当にこのグループを削除しますか？")) return;

    try {
      const groupRef = doc(db, "groups", groupId);
      const groupDoc = await getDoc(groupRef);

      if (!groupDoc.exists()) {
        alert("グループが存在しません。");
        return;
      }

      const groupData = groupDoc.data();

      if (groupData.createdBy !== user.uid) {
        alert("グループを削除できるのは作成者のみです。");
        return;
      }

      await deleteDoc(groupRef);
      setGroups(groups.filter((g) => g.id !== groupId));
      alert("グループを削除しました。");
    } catch (error) {
      console.error("グループ削除エラー:", error);
    }
  };

  // useEffectでグループ取得
  useEffect(() => {
    if (user?.uid) {
      fetchGroups();
    }
  }, [user]);

  return (
    <Container
      className="mt-4"
      style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
    >
      {/* ナビゲーションバー */}
      <Navbar
        bg="light"
        className="p-3 d-flex justify-content-between shadow-sm"
      >
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            className="d-flex align-items-center"
            onClick={() => setShowModal(true)}
          >
            <FaUserPlus /> 友達追加
          </Button>
          <Button
            variant="success"
            className="d-flex align-items-center"
            onClick={() => setIsGroupModalOpen(true)}
          >
            <FaUsers className="me-1" /> グループ作成
          </Button>
        </div>
        <Button variant="danger" size="sm" onClick={handleLogout}>
          <FaSignOutAlt className="me-1" /> ログアウト
        </Button>
      </Navbar>

      {/* ユーザー情報 */}
      {user ? (
        <>
          <div className="d-flex align-items-center mt-4 border p-3 rounded shadow-sm bg-white">
            <Image
              src={user.photoURL}
              roundedCircle
              width={80}
              height={80}
              className="me-3"
            />
            <div>
              <h4>{user.displayName}</h4>
              <Button
                variant="link"
                className="p-0 text-primary"
                onClick={() => setShowUid(!showUid)}
              >
                <FaEye className="me-1" /> UIDを表示
              </Button>
              {showUid && <p className="text-muted mt-1"> {user.uid}</p>}
            </div>
          </div>

          {/* 友達リスト */}
          <h5 className="mt-4">友達リスト</h5>
          {friends.length > 0 ? (
            <ListGroup>
              {friends.map((friend) => (
                <ListGroup.Item
                  key={friend.id}
                  className="d-flex align-items-center justify-content-between bg-light rounded mb-2"
                >
                  <div
                    className="d-flex align-items-center flex-grow-1"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/chat/${friend.id}`)}
                  >
                    <Image
                      src={friend.img}
                      roundedCircle
                      width={50}
                      height={50}
                      className="me-3"
                    />
                    <span>{friend.name}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    {unreadCounts[friend.id] > 0 && (
                      <Badge bg="danger" className="me-2">
                        {unreadCounts[friend.id]}
                      </Badge>
                    )}
                    <Button
                      variant="none"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation(); // navigate防止
                        handleDeleteFriend(friend.id);
                      }}
                    >
                      <FaTrash size={14} className="trash-icon" />
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <p className="text-muted">none</p>
          )}

          {/* グループ一覧 */}
          <h5 className="mt-4">グループ一覧</h5>
          {groups.length > 0 ? (
            <ListGroup>
              {groups.map((group) => (
                <ListGroup.Item
                  key={group.id}
                  className="d-flex align-items-center justify-content-between bg-light rounded mb-2"
                >
                  <div
                    className="d-flex align-items-center flex-grow-1"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/group/${group.id}`)}
                  >
                    <span>{group.name}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    {unreadGroupCounts[group.id] > 0 && (
                      <Badge bg="danger" className="me-2">
                        {unreadGroupCounts[group.id]}
                      </Badge>
                    )}
                    <Button
                      variant="none"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteGroup(group.id);
                      }}
                    >
                      <FaTrash size={14} className="trash-icon" />
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <p className="text-muted">none</p>
          )}
        </>
      ) : (
        <p className="text-center mt-5">Loading...</p>
      )}

      {/* 友達追加モーダル */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>友達を追加</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <FormControl
                type="text"
                placeholder="ユーザーIDを入力"
                value={searchUserId}
                onChange={(e) => setSearchUserId(e.target.value)}
              />
            </Form.Group>
            <Button
              variant="primary"
              className="mt-3"
              onClick={handleFriendSearch}
            >
              <FaSearch className="me-1" /> 追加
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* グループ作成モーダル */}
      <Modal
        show={isGroupModalOpen}
        onHide={() => setIsGroupModalOpen(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>グループ作成</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>グループ名</Form.Label>
              <FormControl
                type="text"
                placeholder="グループ名を入力"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>友達を選択</Form.Label>
              <ListGroup>
                {friends.map((friend) => (
                  <ListGroup.Item key={friend.id}>
                    <Form.Check
                      type="checkbox"
                      label={friend.name}
                      checked={selectedFriends.includes(friend.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFriends([...selectedFriends, friend.id]);
                        } else {
                          setSelectedFriends(
                            selectedFriends.filter((id) => id !== friend.id)
                          );
                        }
                      }}
                    />
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Form.Group>

            <Button
              variant="success"
              className="mt-3"
              onClick={handleCreateGroup}
            >
              <FaPlus className="me-1" /> 作成
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default Profile;
